import { useEffect, useState, useLayoutEffect } from 'react';
import MealPrepLoader from './MealPrepLoader';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext';
import { mergeAppAccess } from '../utils/userAppAccess';
import { DEFAULT_FREE_FEATURES } from '../context/SubscriptionContext';
import Icon from './AppIcon';
import FeatureLockedPage from './FeatureLockedPage';
import TermsAcceptanceModal from './TermsAcceptanceModal';
import BillingModal from './billing/BillingModal';
import BillingBanner from './billing/BillingBanner';
import { useTermsAcceptance } from '../hooks/useTermsAcceptance';
import { isPasswordRecoveryPending } from '../utils/passwordReset';
import StaffLoginRedirect from './StaffLoginRedirect';

const ProtectedRoute = ({ children, requireAdmin = false, requireSuperAdmin = false, requiredFeature = null }) => {
  const { user, role, isLoading, isActive, statusLoaded, logout, organizationId, displayName, userAppAccess, userAppAccessLoaded, onboardingRequired, isOrgAdmin, isCareHomeManager } = useAuth();
  const { hasFeature, appAccess: orgAppAccess, isLoading: subLoading, billingState } = useSubscription();
  const effectiveAppAccess = mergeAppAccess(orgAppAccess, userAppAccess);
  const location = useLocation();
  const {
    isChecking: termsChecking,
    isAccepting: termsAccepting,
    isRequired: termsRequired,
    termsVersion,
    error: termsError,
    acceptTerms,
  } = useTermsAcceptance({
    userId: user?.id,
    organizationId,
    displayName,
    email: user?.email,
  });

  // Check if user is Super Admin
  const isSuperAdmin = role === 'Super Admin';
  const roleKey = String(role || '').toLowerCase();
  const isStaffLike = roleKey === 'staff';
  
  // Check if user is Admin (includes Super Admin, Organization Admin, or legacy admin)
  const isAdmin = isSuperAdmin || role === 'Organization Admin' || role === 'admin';

  // Dismiss the HTML splash once auth finishes (handled globally by AppBootstrap).
  useLayoutEffect(() => {
    if (!isLoading) {
      window.__dismissAppSplash?.();
    }
  }, [isLoading]);

  // While auth is still resolving, return null — the HTML splash covers the
  // screen so the user never sees a blank or double-loader flash.
  if (isLoading) {
    return null;
  }

  if (user && isPasswordRecoveryPending()) {
    return <Navigate to="/reset-password" replace />;
  }

  // If no user after loading is done (or after timeout), redirect to login
  if (!user) {
    return <StaffLoginRedirect />;
  }

  // First-login onboarding gate — newly provisioned customers must finish setup
  if (!isSuperAdmin && onboardingRequired && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  // Check if user is inactive (only after status is loaded)
  if (statusLoaded && isActive === false) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="max-w-md w-full bg-card border border-border rounded-lg p-8 text-center">
          <div className="w-16 h-16 bg-error/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="LockAlert" size={32} color="var(--color-error)" />
          </div>
          <h2 className="text-2xl font-bold text-foreground mb-2">Account Deactivated</h2>
          <p className="text-muted-foreground mb-6">
            Your account has been deactivated by the administrator. Please contact your care home manager for assistance.
          </p>
          <button
            onClick={logout}
            className="w-full px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors font-semibold flex items-center justify-center gap-2"
          >
            <Icon name="LogOut" size={16} />
            Log Out
          </button>
        </div>
      </div>
    );
  }

  if (termsChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-[3px] border-primary/20 border-t-primary rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">Checking your account…</p>
        </div>
      </div>
    );
  }

  if (termsRequired) {
    return (
      <TermsAcceptanceModal
        onAccept={acceptTerms}
        onLogout={logout}
        termsVersion={termsVersion}
        isSubmitting={termsAccepting}
        error={termsError}
        defaultSignerName={displayName || user?.email || ''}
      />
    );
  }

  // Check Super Admin requirement
  if (requireSuperAdmin && !isSuperAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="max-w-md w-full bg-card border border-border rounded-lg p-8 text-center">
          <div className="w-16 h-16 bg-error/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="ShieldOff" size={32} color="var(--color-error)" />
          </div>
          <h2 className="text-2xl font-bold text-foreground mb-2">Access Denied</h2>
          <p className="text-muted-foreground mb-6">
            You don't have permission to access this page. Super Admin privileges required.
          </p>
          <button
            onClick={() => window.history.back()}
            className="w-full px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Check admin requirement
  if (requireAdmin && !isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="max-w-md w-full bg-card border border-border rounded-lg p-8 text-center">
          <div className="w-16 h-16 bg-error/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="ShieldAlert" size={32} color="var(--color-error)" />
          </div>
          <h2 className="text-2xl font-bold text-foreground mb-2">Access Denied</h2>
          <p className="text-muted-foreground mb-6">
            You don't have permission to access this page. Admin privileges required.
          </p>
          <button
            onClick={() => window.history.back()}
            className="w-full px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Check feature subscription
  if (requiredFeature && isStaffLike && !DEFAULT_FREE_FEATURES.includes(requiredFeature)) {
    return <FeatureLockedPage featureKey={requiredFeature} />;
  }

  // App-level access gate (organization subscription + per-user system access)
  if (!isSuperAdmin && !subLoading && userAppAccessLoaded && effectiveAppAccess?.mealManager === false) {
    const userOnlyBlocked = orgAppAccess?.mealManager !== false && userAppAccess?.mealManager === false;
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="max-w-md w-full bg-card border border-border rounded-lg p-8 text-center">
          <div className="w-16 h-16 bg-error/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="Ban" size={32} color="var(--color-error)" />
          </div>
          <h2 className="text-2xl font-bold text-foreground mb-2">Meal Manager Access Revoked</h2>
          <p className="text-muted-foreground mb-6">
            {userOnlyBlocked
              ? 'Your account does not have permission to use Meal Manager. Please contact your Super Admin.'
              : 'Your organization no longer has access to Meal Manager. Please contact your Super Admin.'}
          </p>
          <button
            onClick={logout}
            className="w-full px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors font-semibold flex items-center justify-center gap-2"
          >
            <Icon name="LogOut" size={16} />
            Log Out
          </button>
        </div>
      </div>
    );
  }

  // Check feature subscription
  if (requiredFeature && !subLoading && !hasFeature(requiredFeature)) {
    return <FeatureLockedPage featureKey={requiredFeature} />;
  }

  const skipBillingUi = isSuperAdmin
    || location.pathname === '/onboarding'
    || location.pathname === '/billing'
    || location.pathname === '/billing/complete';
  const canPayBilling = isOrgAdmin || isCareHomeManager;

  return (
    <>
      {!skipBillingUi && (
        <BillingGate
          billingState={billingState}
          canPay={canPayBilling}
          organizationId={organizationId}
          logout={logout}
        />
      )}
      {billingState?.blocking && !skipBillingUi ? null : children}
    </>
  );
};

function BillingGate({ billingState, canPay, organizationId, logout }) {
  const [billingModalOpen, setBillingModalOpen] = useState(false);
  // Grace only shows the banner — do not auto-open the modal or it covers the profile menu.
  const showBillingModal = Boolean(billingState?.blocking)
    || billingModalOpen;

  return (
    <>
      <BillingBanner
        billing={billingState}
        canPay={canPay}
        onAddCard={() => setBillingModalOpen(true)}
      />
      <BillingModal
        open={showBillingModal}
        blocking={Boolean(billingState?.blocking)}
        organizationId={organizationId}
        billing={billingState}
        canPay={canPay}
        onClose={() => {
          if (!billingState?.blocking) {
            setBillingModalOpen(false);
          }
        }}
        onLogout={logout}
      />
    </>
  );
}

export default ProtectedRoute;
